import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import {
  detectChartVersion,
  parseChartV2,
  serializeChartV2,
  v2BlocksToEditFormat,
  blocksToV2Format,
  generateBlockId
} from '../utils/chartParser';

const ChartDialog = ({ isOpen, onClose, onSave, onDelete, initialChart = '' }) => {
  const [chart, setChart] = useState('');
  const [editBlocks, setEditBlocks] = useState([]);
  const [chartVersion, setChartVersion] = useState(2); // Default to V2 for new charts
  const [activeBlockId, setActiveBlockId] = useState(null); // For click-to-edit

  // Ref for click-outside detection
  const editorContainerRef = useRef(null);

  useEffect(() => {
    if (initialChart) {
      const version = detectChartVersion(initialChart);
      setChartVersion(version);
      if (version === 2) {
        const parsed = parseChartV2(initialChart);
        setEditBlocks(v2BlocksToEditFormat(parsed.blocks));
      } else {
        setChart(initialChart);
      }
    } else {
      // New chart - default to V2 with one empty section
      setChartVersion(2);
      const newBlockId = generateBlockId();
      setEditBlocks([{
        id: newBlockId,
        header: 'Verse',
        content: ''
      }]);
      setActiveBlockId(newBlockId); // Auto-focus the first block
      setChart('');
    }
  }, [initialChart]);

  // Handle click outside to exit edit mode for a block
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (activeBlockId && editorContainerRef.current) {
        const activeBlockElement = editorContainerRef.current.querySelector(`[data-block-id="${activeBlockId}"]`);
        if (activeBlockElement && !activeBlockElement.contains(e.target)) {
          setActiveBlockId(null);
        }
      }
    };

    if (chartVersion === 2) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [activeBlockId, chartVersion]);

  // Handle Escape key to exit edit mode
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && activeBlockId) {
        setActiveBlockId(null);
      }
    };

    if (chartVersion === 2) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [activeBlockId, chartVersion]);

  const handleSave = () => {
    if (chartVersion === 2) {
      const { blocks, layout } = blocksToV2Format(editBlocks);
      const v2Json = serializeChartV2(blocks, layout);
      onSave(v2Json);
    } else {
      onSave(chart);
    }
    onClose();
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this chart?')) {
      onDelete();
      onClose();
    }
  };

  // V2 Block Editor handlers
  const handleAddBlock = () => {
    const newBlock = {
      id: generateBlockId(),
      header: 'New Section',
      content: ''
    };
    setEditBlocks([...editBlocks, newBlock]);
    setActiveBlockId(newBlock.id);
  };

  const handleUpdateBlock = (blockId, field, value) => {
    setEditBlocks(editBlocks.map(block =>
      block.id === blockId ? { ...block, [field]: value } : block
    ));
  };

  const handleDeleteBlock = (blockId) => {
    if (editBlocks.length <= 1) {
      alert('Cannot delete the last block. A chart must have at least one block.');
      return;
    }
    setEditBlocks(editBlocks.filter(block => block.id !== blockId));
    if (activeBlockId === blockId) {
      setActiveBlockId(null);
    }
  };

  // Render a single V2 block for edit mode (click-to-edit inline)
  const renderV2EditBlock = (block, index) => {
    const isActive = activeBlockId === block.id;

    if (isActive) {
      // Edit mode for this block
      return (
        <div
          key={block.id}
          data-block-id={block.id}
          className="mb-4 border-2 border-blue-400 rounded-lg p-4 bg-blue-50"
        >
          <div className="flex items-center justify-between mb-3">
            <input
              type="text"
              value={block.header}
              onChange={(e) => handleUpdateBlock(block.id, 'header', e.target.value)}
              className="text-xl font-semibold bg-white border border-gray-300 rounded px-2 py-1 flex-1 mr-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Section header..."
              autoFocus
            />
            <button
              onClick={() => handleDeleteBlock(block.id)}
              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-100 rounded"
              title="Delete section"
            >
              <TrashIcon className="w-5 h-5" />
            </button>
          </div>
          <textarea
            value={block.content || ''}
            onChange={(e) => handleUpdateBlock(block.id, 'content', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm resize-none bg-white"
            rows={6}
            placeholder="Chords and lyrics..."
          />
        </div>
      );
    }

    // View mode for this block (click to edit)
    return (
      <div
        key={block.id}
        data-block-id={block.id}
        onClick={() => setActiveBlockId(block.id)}
        className="mb-4 border border-transparent hover:border-gray-300 rounded-lg p-4 cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <h2 className="text-xl font-semibold mb-2">{block.header || 'Section'}</h2>
        {block.content ? (
          <pre className="font-mono text-sm whitespace-pre-wrap bg-gray-50 p-3 rounded">
            {block.content}
          </pre>
        ) : (
          <p className="text-gray-400 italic text-sm">Click to add content...</p>
        )}
      </div>
    );
  };

  // Render V2 block editor with click-to-edit
  const renderBlockEditor = () => {
    return (
      <div ref={editorContainerRef} className="flex flex-col h-full overflow-auto">
        <div className="space-y-2 pb-4">
          {editBlocks.map((block, index) => renderV2EditBlock(block, index))}
        </div>

        <button
          onClick={handleAddBlock}
          className="flex items-center justify-center space-x-2 w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          <span>Add Section</span>
        </button>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-semibold text-gray-900">
              {initialChart ? 'Edit Chart' : 'Add Chart'}
            </h3>
            <span className={`text-xs px-2 py-1 rounded ${chartVersion === 2 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
              V{chartVersion}
            </span>
            {chartVersion === 2 && <span className="text-sm font-normal text-green-600">(Click a section to edit)</span>}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 p-6 overflow-hidden">
          <div className="h-full flex flex-col">
            {chartVersion === 2 ? (
              renderBlockEditor()
            ) : (
              <>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chart (Markdown)
                </label>
                <textarea
                  value={chart}
                  onChange={(e) => setChart(e.target.value)}
                  className="flex-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm resize-none"
                  style={{ minHeight: '400px' }}
                  placeholder="Enter your chart in markdown format..."
                />
              </>
            )}
          </div>
        </div>

        <div className="flex justify-between items-center p-6 border-t">
          <div>
            {initialChart && (
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-red-600 bg-red-50 rounded-md hover:bg-red-100 flex items-center space-x-2"
              >
                <TrashIcon className="w-4 h-4" />
                <span>Delete Chart</span>
              </button>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Save Chart
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartDialog;
